import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CameraCapture from '../components/CameraCapture';
import { useExpenses } from '../contexts/ExpenseContext';
import { CATEGORIES, TRAVEL_SUBCATEGORIES, ExpenseCategory, TravelSubcategory } from '../types';
import { 
  CheckCircle, 
  Loader2,
  AlertCircle,
  ChevronLeft
} from 'lucide-react';

export default function Scanner() {
  const navigate = useNavigate();
  const { addExpense } = useExpenses();
  
  const [step, setStep] = useState<'camera' | 'details' | 'success'>('camera');
  const [capturedImage, setCapturedImage] = useState<{ blob: Blob; filename: string } | null>(null);
  const [category, setCategory] = useState<ExpenseCategory>('travel');
  const [subcategory, setSubcategory] = useState<TravelSubcategory>('uber');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleCapture = (blob: Blob, filename: string) => {
    setCapturedImage({ blob, filename });
    setStep('details');
  };

  const handleCancel = () => {
    navigate('/');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!capturedImage) return;

    setIsSubmitting(true);
    setError('');

    try {
      await addExpense({
        category,
        subcategory: category === 'travel' ? subcategory : undefined,
        description,
        amount: amount ? parseFloat(amount) : undefined,
        date,
        file: capturedImage.blob,
        filename: capturedImage.filename,
      });

      setStep('success');
      setTimeout(() => navigate('/expenses'), 2000);
    } catch (err) {
      setError('Failed to save expense. Please try again.');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (step === 'success') {
    return (
      <div className="max-w-md mx-auto text-center py-20 animate-fade-in">
        <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-green-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">
          Saved Successfully!
        </h2>
        <p className="text-white/60">Redirecting to expenses...</p>
      </div>
    );
  }

  if (step === 'camera') {
    return (
      <div className="max-w-2xl mx-auto animate-fade-in">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">Scan Receipt</h1>
          <p className="text-white/60 mt-1">Position the receipt in the viewfinder</p>
        </div>
        <CameraCapture onCapture={handleCapture} onCancel={handleCancel} />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      <button
        onClick={() => setStep('camera')}
        className="flex items-center space-x-2 text-white/70 hover:text-white mb-6 transition-colors"
      >
        <ChevronLeft className="w-5 h-5" />
        <span>Retake Photo</span>
      </button>

      <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 overflow-hidden">
        {/* Image Preview */}
        {capturedImage && (
          <div className="aspect-video bg-black flex items-center justify-center">
            <img
              src={URL.createObjectURL(capturedImage.blob)}
              alt="Captured receipt"
              className="max-h-full max-w-full object-contain"
            />
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="flex items-center space-x-2 p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-300 text-sm">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">
              Category
            </label>
            <div className="grid grid-cols-4 gap-2">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => setCategory(cat.value)}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${
                    category === cat.value
                      ? 'border-shuttle-sand bg-shuttle-sand/20'
                      : 'border-white/20 hover:border-white/40'
                  }`}
                >
                  <span className="text-xl block">{cat.icon}</span>
                  <span className="text-xs text-white/70 mt-1 block truncate">{cat.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Travel Subcategory */}
          {category === 'travel' && (
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                Travel Type
              </label>
              <div className="grid grid-cols-4 gap-2">
                {TRAVEL_SUBCATEGORIES.map((sub) => (
                  <button
                    key={sub.value}
                    type="button"
                    onClick={() => setSubcategory(sub.value)}
                    className={`p-2 rounded-xl border-2 text-center transition-all ${
                      subcategory === sub.value
                        ? 'border-shuttle-sand bg-shuttle-sand/20'
                        : 'border-white/20 hover:border-white/40'
                    }`}
                  >
                    <span className="text-lg block">{sub.icon}</span>
                    <span className="text-xs text-white/70 truncate block">{sub.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-white/80 mb-1">
              Description
            </label>
            <input
              id="description"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., Uber from airport to hotel"
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:ring-2 focus:ring-shuttle-sand focus:border-shuttle-sand transition-all"
            />
          </div>

          {/* Amount and Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="amount" className="block text-sm font-medium text-white/80 mb-1">
                Amount ($)
              </label>
              <input
                id="amount"
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:ring-2 focus:ring-shuttle-sand focus:border-shuttle-sand transition-all"
              />
            </div>
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-white/80 mb-1">
                Date
              </label>
              <input
                id="date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:ring-2 focus:ring-shuttle-sand focus:border-shuttle-sand transition-all"
              />
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex items-center justify-center space-x-2 py-4 px-6 bg-gradient-to-r from-shuttle-sand to-amber-500 text-shuttle-dark rounded-xl font-medium hover:from-amber-400 hover:to-amber-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                <span>Save Expense</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}





